import React, {useMemo} from 'react'
import PropTypes from 'prop-types'
import {Box, Select, SimpleGrid, useStyleConfig} from '@chakra-ui/react'
import {useIntl} from 'react-intl'
import useInView from '../../../hooks/use-in-view'
import {remCalc} from '../../../theme/_utils'

const EMPTY_TILE_VALUE = ''

const getProductKey = (product) => product?.productId || product?.id

/**
 * Resolves which dropdown option (if any) matches the product currently shown in a slot,
 * first by master product ID (stable across color/variant swaps), then by product ID.
 * @param {Object} product - product currently displayed in the compare tile
 * @param {Array} options - available compare dropdown options
 * @returns {string} selectionId of the matching option, or an empty string
 */
const getSelectedValueForSlot = (product, options) => {
    if (!product) return EMPTY_TILE_VALUE

    // Both PLP hits and product responses expose the master through this custom field.
    // The standard master object is absent with the compare flow's expand parameters.
    const masterId = product?.master?.masterId || product?.c_prdha_category
    const productId = getProductKey(product)

    const matchByMaster = masterId && options.find((option) => option.masterId === masterId)
    if (matchByMaster) return matchByMaster.selectionId

    const matchById = options.find((option) => option.selectionId === productId)
    return matchById ? matchById.selectionId : EMPTY_TILE_VALUE
}

const CompareSelectGrid = ({
    compareColumns,
    compareGridMaxWidth,
    selectedValues,
    compareOptions,
    onSelectChange,
    gridStyles,
}) => {
    const intl = useIntl()

    return (
        <SimpleGrid
            {...gridStyles}
            columns={compareColumns}
            templateColumns={`repeat(${compareColumns}, minmax(0, 1fr))`}
            maxW={compareGridMaxWidth}
            mx="auto"
        >
            {Array.from({length: compareColumns}, (_, index) => (
                <Select
                    key={`compare-select-${index}`}
                    value={selectedValues[index] || EMPTY_TILE_VALUE}
                    onChange={(event) => onSelectChange(index, event.target.value)}
                    isDisabled={compareOptions.length === 0}
                    bg="#FFFFFF"
                    color="#13183A"
                    w="100%"
                    maxWidth={remCalc(400)}
                    justifySelf="center"
                    boxShadow="0 0 0 1px var(--border-neutral-highlight, #FFF) inset, 0 1px 2px 0 var(--neutral-xs, rgba(19, 24, 58, 0.08)), 0 0 0 1px var(--neutral-xs, rgba(19, 24, 58, 0.08))"
                    borderWidth="1px"
                    borderStyle="solid"
                    borderColor="#FFFFFF"
                    borderRadius="12px"
                >
                    <option value="" disabled hidden>
                        {intl.formatMessage({
                            id: 'compare-modal.select.placeholder',
                            defaultMessage: 'Select a product',
                        })}
                    </option>
                    {compareOptions.map((option) => (
                        <option key={option.selectionId} value={option.selectionId}>
                            {option.masterName}
                        </option>
                    ))}
                </Select>
            ))}
        </SimpleGrid>
    )
}

CompareSelectGrid.propTypes = {
    compareColumns: PropTypes.number.isRequired,
    compareGridMaxWidth: PropTypes.string.isRequired,
    selectedValues: PropTypes.array.isRequired,
    compareOptions: PropTypes.array.isRequired,
    onSelectChange: PropTypes.func.isRequired,
    gridStyles: PropTypes.object,
}

const CompareSelectBar = ({
    compareColumns,
    compareGridMaxWidth,
    compareSlots,
    compareOptions,
    onSelectProduct,
}) => {
    const styles = useStyleConfig('ProductComparePlp')

    const selectedValues = useMemo(
        () => compareSlots.map((product) => getSelectedValueForSlot(product, compareOptions)),
        [compareSlots, compareOptions],
    )

    // sentinel marks where the bar sits in normal flow; once it scrolls above the viewport
    // (top < 0) we mirror it in a bar fixed to the top of the screen
    const [sentinelRef, sentinelEntry] = useInView({threshold: 0})

    const showFixedBar = Boolean(
        sentinelEntry && !sentinelEntry.isIntersecting && sentinelEntry.boundingClientRect.top < 0,
    )

    return (
        <>
            <Box ref={sentinelRef} {...styles.modalStickySelectSection}>
                <CompareSelectGrid
                    compareColumns={compareColumns}
                    compareGridMaxWidth={compareGridMaxWidth}
                    selectedValues={selectedValues}
                    compareOptions={compareOptions}
                    onSelectChange={onSelectProduct}
                    gridStyles={styles.modalStickySelectGrid}
                />
            </Box>

            {showFixedBar && (
                <Box {...styles.modalStickySelectBarFixed}>
                    <Box {...styles.modalSelectBarFixedInset}>
                        <CompareSelectGrid
                            compareColumns={compareColumns}
                            compareGridMaxWidth={compareGridMaxWidth}
                            selectedValues={selectedValues}
                            compareOptions={compareOptions}
                            onSelectChange={onSelectProduct}
                            gridStyles={styles.modalStickySelectGrid}
                        />
                    </Box>
                </Box>
            )}
        </>
    )
}

CompareSelectBar.propTypes = {
    compareColumns: PropTypes.number.isRequired,
    compareGridMaxWidth: PropTypes.string.isRequired,
    compareSlots: PropTypes.array,
    compareOptions: PropTypes.arrayOf(
        PropTypes.shape({
            masterId: PropTypes.string,
            masterName: PropTypes.string,
            selectionId: PropTypes.string,
        }),
    ),
    onSelectProduct: PropTypes.func,
}

CompareSelectBar.defaultProps = {
    compareSlots: [],
    compareOptions: [],
    onSelectProduct: () => {},
}

export default CompareSelectBar
